import { motion } from "framer-motion";

const industries = [
  { name: "Healthcare", desc: "Patient flows, compliance, scheduling" },
  { name: "Manufacturing", desc: "Production tracking, BOM, quality" },
  { name: "Retail", desc: "POS, inventory, omnichannel" },
  { name: "Logistics", desc: "Fleet, dispatch, route optimization" },
  { name: "Finance", desc: "Risk, KYC, portfolio operations" },
  { name: "Agencies", desc: "Projects, retainers, client portals" },
  { name: "Startups", desc: "Lean ops, runway, growth funnels" },
  { name: "Enterprises", desc: "Multi-entity, governance, scale" },
];

const images = [
  "https://images.unsplash.com/photo-1598257212525-34d72ccf27c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwd2hpdGUlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc4NDAxODY0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1637489415491-f9622f751216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwd2hpdGUlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc4NDAxODY0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1602577121285-aca49920efac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwd2hpdGUlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc4NDAxODY0fDA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/950241/pexels-photo-950241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function Industries() {
  return (
    <section
      id="industries"
      data-testid="industries-section"
      className="bg-neutral-50 text-neutral-950 py-24 md:py-36 border-t border-neutral-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-neutral-500">
              02 — Industries
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 leading-[1.05]">
              Built for the way
              <br />
              <span className="text-neutral-400">you actually work.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-lg text-neutral-600 font-light leading-relaxed">
              From single-location clinics to multi-entity manufacturers — NOEX is
              implemented per business, never per industry template.
            </p>
          </div>
        </div>

        {/* Image collage */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-16">
          {images.map((src, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className={`${
                i === 0 ? "md:col-span-5 md:row-span-2 h-72 md:h-[480px]" :
                i === 1 ? "md:col-span-7 h-72 md:h-[230px]" :
                i === 2 ? "md:col-span-3 h-56 md:h-[230px]" :
                "md:col-span-4 h-56 md:h-[230px]"
              } relative overflow-hidden rounded-2xl border border-neutral-200 group`}
            >
              <img
                src={src}
                alt="Industry"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
              />
            </motion.div>
          ))}
        </div>

        {/* Industry list */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-neutral-200">
          {industries.map((ind, i) => (
            <div
              key={i}
              data-testid={`industry-${ind.name.toLowerCase()}`}
              className={`py-8 md:py-10 px-2 border-b border-neutral-200 ${
                (i + 1) % 2 !== 0 ? "border-r md:border-r-0" : ""
              } ${
                i % 4 !== 3 ? "md:border-r" : ""
              } group cursor-default`}
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-xl md:text-2xl font-normal tracking-tight group-hover:text-neutral-500 transition-colors">
                  {ind.name}
                </h3>
              </div>
              <p className="text-sm text-neutral-500 font-light leading-relaxed">
                {ind.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
